/**
 * Rollup completion drawer — the "click through campus by campus" flow
 * behind a collapsed board ticket (one per day / mealtime / drop).
 *
 * Renders the same lazy per-subKind sheet bodies the single-task
 * `TaskDetailDrawer` uses, wrapped in a `WorkflowStepList` rail so the
 * operator steps through each member task in turn. A step's sheet fires
 * `onCompleted` when its work is actually submitted — the drawer marks
 * the step done and advances to the next open one; `onClose` (Cancel /
 * the X) closes the whole drawer, and every individual task stays
 * reachable by tapping its step chip directly.
 */

import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@ordo/ui/sheet'
import { WorkflowStepList } from '@ordo/ui/workflow-step-list'
import { Suspense, useEffect, useMemo, useState } from 'react'

import { nextOpenStepId, rollupStepLabel, type TaskRollupRow } from './helpers'
import { resolveSheetComponent, TaskSheetSkeleton } from './task-detail-drawer'

interface TaskRollupDrawerProps {
  rollup: TaskRollupRow | null
  onClose: () => void
}

export function TaskRollupDrawer({ rollup, onClose }: TaskRollupDrawerProps) {
  const [doneIds, setDoneIds] = useState<ReadonlySet<string>>(new Set())
  const [selectedId, setSelectedId] = useState<string | null>(null)

  // Reset wizard progress whenever a different rollup ticket opens.
  const rollupId = rollup?.id ?? null
  useEffect(() => {
    setDoneIds(new Set())
    setSelectedId(null)
  }, [rollupId])

  const tasks = rollup?.rollupTasks ?? []
  const current = tasks.find((t) => t.id === selectedId) ?? tasks[0] ?? null
  const steps = useMemo(
    () =>
      tasks.map((t) => ({
        id: t.id,
        title: rollupStepLabel(t),
        done: doneIds.has(t.id),
      })),
    [tasks, doneIds],
  )
  const doneCount = steps.filter((s) => s.done).length
  const SheetBody = current ? resolveSheetComponent(current.subKind) : null

  function handleStepCompleted(taskId: string) {
    const nextDone = new Set(doneIds)
    nextDone.add(taskId)
    setDoneIds(nextDone)
    const nextId = nextOpenStepId(tasks, nextDone, taskId)
    if (nextId === null) {
      onClose()
      return
    }
    setSelectedId(nextId)
  }

  return (
    <Sheet open={rollup !== null} onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="right" size="md" className="flex flex-col gap-4">
        {rollup && current && SheetBody ? (
          <>
            <SheetHeader className="pr-8">
              <SheetTitle className="text-base leading-tight">{rollup.title}</SheetTitle>
              <SheetDescription>
                {doneCount} of {tasks.length} done — work through each step, or jump to one directly.
              </SheetDescription>
            </SheetHeader>
            <WorkflowStepList
              steps={steps}
              selectedId={current.id}
              onSelect={setSelectedId}
              orientation="horizontal"
              ariaLabel="Rollup steps"
            />
            <div className="min-h-0 flex-1">
              <Suspense fallback={<TaskSheetSkeleton />}>
                <SheetBody
                  key={current.id}
                  task={current}
                  onClose={onClose}
                  onCompleted={() => handleStepCompleted(current.id)}
                />
              </Suspense>
            </div>
          </>
        ) : null}
      </SheetContent>
    </Sheet>
  )
}
